/**
 * A non-secret, readable marker that this browser was signed in — set beside
 * the real session cookie by server/auth/routes.ts.
 *
 * The session cookie itself is httpOnly and signed, so the client cannot see
 * it and cannot know whether it is still valid without asking the server. That
 * question costs a round trip, and blocking the first paint on it is what made
 * every reload show "Loading…" before any content.
 *
 * SECURITY: this grants nothing. Its value is the literal "1", it is never
 * consulted by the server, and requireAuth still gates every API route. Forging
 * it buys an empty shell whose every request answers 401. It decides only what
 * we are willing to paint before the server has replied.
 */

const HINT_COOKIE = 'session_hint'

function cookies(): string[] {
  try {
    return document.cookie.split(';')
  } catch {
    return []
  }
}

/** Whether this browser looks signed in. Optimistic, not authoritative. */
export function hasSessionHint(): boolean {
  return cookies().some((pair) => pair.trim() === `${HINT_COOKIE}=1`)
}

/**
 * Drops the hint after the server has told us the session is gone. Without
 * this, a lapsed session would paint the shell and bounce to the login gate on
 * every single load.
 */
export function clearSessionHint(): void {
  try {
    document.cookie = `${HINT_COOKIE}=; Max-Age=0; path=/`
  } catch {
    // Cookies disabled — the session check still runs, we just pay the round
    // trip before painting, which is the old behaviour.
  }
}
